import Amplify, { Auth, Hub } from "aws-amplify";
import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import "./assets/scss/style.css";
import { devApi, prodApi, qaApi } from "./aws-export";
import Layout from "./components/layout";
import LogoutScreen from "./components/logout";
import ThemeComponent from "./components/theme";
import moment from "moment-timezone"
import ErrorBoundaryComponent from "./ErrorBoundary";
import { RootState, useAppDispatch, useAppSelector } from "./store/store";
import { userAuthDetails } from "./store/thunk/authThunk";
import { useSelector } from "react-redux";
import FederatedSignIn from "./components/federatedSignIn/federatedSignIn";
import { manageTheme } from "./store/slices/themeSlice";


import MainDashboard from "./pages/mainDashboard";
import version from "../package.json";
import CustomLoader from "./components/customLoader";
import { findMenuNameFromUrl } from "./components/layout/menuItems";
import NotificationRulesPage from "./pages/notificationRules";
import RedirectionLoader from "./components/redirectionLoader";
import SsoDisabled from "./components/sso/ssoDisabled";

import { setInitialLogin, setUserAlreadyLogged } from "./store/slices/authSlice";
import { setSSOisEnable } from "./store/slices/commonSlice";
import { updateUserHistoryData } from "./store/thunk/tenantReport";
import DetectionPage from "./pages/detection";
import FacilityDashboard from "./pages/facilityDashboard";
import FacilityMainView from "./pages/MainView";
import ElementDetails from "./components/elementDetection/Details";
import VerifyLoginLinkComponent from "./components/verifyLoginLink";
import { checkAuth } from "./store/thunk/commonThunk";
import CategoryManagementPage from "./pages/groupsSubstance";
import DeviceIntegrationComponent from "./components/deviceIntegration";
import LLMConfiguration from "./pages/ieqGptConfiguration/components/llm";
// import NotificationPage from "./pages/notificationsManagement";
// ReactGA.initialize(TRACKING_ID);
const loginwithtype = localStorage.getItem("loginwith") || "";
Amplify.configure(
  loginwithtype === "qa" ? qaApi : loginwithtype === "dev" ? devApi : prodApi
);
const TileManagerPage = lazy(() => import("./pages/tileManagement"));
const OverviewPage = lazy(() => import("./pages/overview"));
const Device = lazy(() => import("./pages/devices"));
const InternalIdsPage = lazy(() => import("./pages/InternalIds"));
const EventMap = lazy(() => import("./pages/eventMap"));
const Detection = lazy(() => import("./pages/detenction"));
const Location = lazy(() => import("./pages/location"));
const RoleManagement = lazy(() => import("./pages/roleManagement"));
const ThresholdPage = lazy(() => import("./pages/threshold"));
const SubstanceHistory = lazy(() => import("./pages/substanceHistory"));
const CorePage = lazy(() => import("./pages/newGrafana/core"));
const RootPage = lazy(() => import("./pages/newGrafana/root"));
const SendEmailPage = lazy(() => import("./pages/sendEmail"));
const ScheduleReportListPage = lazy(() => import("./pages/scheduleReports"));

const DashboardDownLoadPage = lazy(() => import("./pages/dashboardDownload"));
const AdminDashboard = lazy(() => import("./pages/adminDashboard"));
const AdminList = lazy(() => import("./pages/adminList"));
const DeviceConfig = lazy(() => import("./pages/deviceConfig"));
const ForgetPassword = lazy(() => import("./pages/forgetPassword"));
const Login = lazy(() => import("./pages/login"));
const Profile = lazy(() => import("./pages/profile"));
const SignUp = lazy(() => import("./pages/signUp"));
const TenantList = lazy(() => import("./pages/tenantList"));
const UserList = lazy(() => import("./pages/userList"));
const NotificationPage = lazy(() => import("./pages/notificationsManagement"));
const QueryPage = lazy(() => import("./pages/query"));
const SessionExpiredPage = lazy(() => import("./pages/sessionExpired"));
const Summary = lazy(() => import("./pages/summary"));
const DeviceHistoryPage = lazy(() => import("./pages/deviceHistory"));
const DevicesVersionPage = lazy(() => import("./pages/deviceVersion"));
const EmailTemplatePage = lazy(() => import("./pages/EmailTemplates/EmailTemplatePage"));
const ExportHistory = lazy(() => import("./pages/exportHistory"));
const GenerateNotificationPage = lazy(() => import("./pages/generateFakeNotification"));
const NewGrafana = lazy(() => import("./pages/newGrafana"));
const TenantReportPage = lazy(() => import("./pages/TenantReport"));
const IeqGPT = lazy(() => import("./pages/ieqGptConfiguration/components/chat"));
const Topics = lazy(() => import("./pages/ieqGptConfiguration/components/topic"));
const Contents = lazy(() => import("./pages/ieqGptConfiguration/components/content"));

const App = () => {
  const dispatch = useAppDispatch();
  const [auth, setAuth] = useState(null);
  const [count, setCount] = useState(0);
  const [isReady, setReady] = useState(false);
  const { autherise, ssoenabled } = useAppSelector(
    (state) => state.commonSlice
  );
  const [isSSO, setIsSSO] = useState(false);
  const { userData, userAlreadyLogged, loading } = useAppSelector((state) => state.authReducer);
  const { totalTime, prevPageTime } = useAppSelector((state: RootState) => state.timeTrackerSlice);
  const { themeMode } = useSelector((state: RootState) => state.themeSlice);
  const [loader, setLoader] = useState<boolean>(false);
  const provider: any = localStorage.getItem("provider");
  const location = useLocation();
  const queryString = location.search;

  // console block for production
  if (loginwithtype === 'prod') {
    console.log = () => { }
    console.error = () => { }
    console.debug = () => { }
  }

  useEffect(() => {
    if (window?.location?.pathname === "/eventMap") {
      setLoader(false);
    } else {
      setLoader(loading);
    }
  }, [loading]);
  useEffect(() => {
    if (userData?.group !== 'admin' && totalTime > 0) {
      const page = findMenuNameFromUrl(prevPageTime?.url);
      const request = {
        tenant_id: userData.tenantId,
        username: userData.username,
        event_type: "web_summary",
        spend_time: totalTime,
        url: prevPageTime?.url,
        page,
      }
      dispatch(updateUserHistoryData(request))
    }
  }, [prevPageTime?.totalTime, userData]);

  useEffect(() => {
    Auth.currentAuthenticatedUser({ bypassCache: false })
      .then(async (user: any) => {
        setAuth(user?.attributes);
        dispatch(userAuthDetails({ user: user }));
        setReady(true);
      })
      .catch((err) => {
        setReady(true);
      });
  }, [autherise]);

  useEffect(() => {
    if (autherise === true && userData !== null && count < 1) {
      setCount(count + 1)
      if (userData?.group !== 'admin') {
        const request = {
          tenant_id: userData.tenantId,
          username: userData.username,
          event_type: "login_click",
          mobile: {
            os: "web",
            version: version.version
          }
        }
        dispatch(updateUserHistoryData(request))
      }
    }
  }, [autherise, userData, count]);

  useEffect(() => {
    let redirectSlug = "";
    if (userData && userData.permission && window.location.pathname === "/") {
      let ids = JSON.parse(
        userData.permission !== null ? userData.permission : "[]"
      );
      ids.sort();
      const firstPermissionId = ids.length ? ids[0] : "";

      if (firstPermissionId) {
        if (firstPermissionId === "1") {
          redirectSlug = "/tenantList";
        } else if (firstPermissionId === "2") {
          redirectSlug = "/userList";
        } else if (firstPermissionId === "3") {
          redirectSlug = "/roleManagement";
        } else if (firstPermissionId === "4") {
          redirectSlug = "/devices";
        } else if (firstPermissionId === "5") {
          redirectSlug = "/deviceConfig";
        } else if (firstPermissionId === "6") {
          redirectSlug = "/location";
        } else if (firstPermissionId === "7") {
          redirectSlug = "/detection";
        } else if (firstPermissionId === "8") {
          redirectSlug = "/groupsSubstance";
        } else {
          redirectSlug = "/";
        }
      }

      if (userData.group === "admin") {
        window.location.pathname = "/ops-dashboard";
      }
    }
  }, [userData]);

  useEffect(() => {
    const body = document.querySelector("body");
    if (themeMode === "dark") {
      body?.classList.add("dark-mode");
      body?.classList.remove("light-mode");
    } else {
      body?.classList.remove("dark-mode");
      body?.classList.add("light-mode");
    }
  }, [themeMode]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "amplify-signout" || event.key === 'amplify-signin') {
        window.location.reload(); // Redirect all tabs to login
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    const theme = localStorage.getItem("theme");
    dispatch(manageTheme(theme));
    async function checkTenantSSO() {
      try {
        setIsSSO(true);
        if (userData && userData.public_sso === null) {
          dispatch(setSSOisEnable(false));
          return;
        } else if (
          userData &&
          userData.public_sso &&
          userData.public_sso !== "true"
        ) {
          dispatch(setSSOisEnable(false));
          return;
        }
      } catch (err) { }
    }
    (async () => {
      try {
        if (
          ["Google", "SignInWithApple", "Facebook"].includes(provider) &&
          userData.group !== "admin"
        ) {
          checkTenantSSO();
        } else {
          setIsSSO(true);
        }
      } catch (err) { }
    })();

    // Subscribe to authentication events
    Hub.listen("auth", ({ payload: { event, data, message } }) => {
      console.log("event ", event);
      if (event === "signIn") {
        dispatch(setInitialLogin(true));
        dispatch(setUserAlreadyLogged(false))
        localStorage.setItem("amplify-signin", Date.now().toString());
      }
      if (event === "signOut") {
        dispatch(setInitialLogin(true));
        localStorage.setItem("amplify-signout", Date.now().toString()); // Notify other tabs
        localStorage.removeItem("amplify-signin"); // Optional: Clear login event    
      }
    });
    //   console.log("userData ", userData);
    //   if(event !== "signIn" && isReady && userData == null && ['Google','SignInWithApple','Facebook'].includes(provider)) {
    //     dispatch(
    //       putNotification({
    //         status: false,
    //         msg: "Email does not exist",
    //       })
    //     );
    //   }
    // });
  }, [userData]);

  // useEffect(() => {
  //   dispatch(getSubStanceList({}));
  // }, []);

  return (
    <ErrorBoundaryComponent>
      <ThemeComponent>
        {isReady ? (
          <>
            <Suspense fallback={<CustomLoader />}>
              {auth ? (
                userData && isSSO ? (
                  userData.isActive === "true" || userData.group === "admin" ? (
                    ssoenabled ? (
                      <>
                        {/* <>{loader && <CustomLoader />}</> */}
                        <Layout hidden={location.pathname === "/verify"}>
                          <Routes>
                            {/* {userData.group === "admin" ? (
                          <Route path="/" element={<AdminDashboard />} />
                        ) : (
                          <Route path="/" element={<Dashboard />} />
                        )} */}
                            <Route
                              path="/login"
                              element={<Navigate to="/dashboard" />}
                            />
                            <Route
                              path="/ops-dashboard"
                              element={<AdminDashboard />}
                            />
                            {/* <Route path="/dashboard" element={<Dashboard />} /> */}
                            <Route
                              path="/analytics/timeseriesdetail"
                              element={<NewGrafana />}
                            />
                            <Route path="/analytics/core" element={<CorePage />} />
                            <Route path="/analytics/root" element={<RootPage />} />
                            <Route path="/generate-notification" element={<GenerateNotificationPage />} />

                            <Route
                              path="/substanceHistory"
                              element={<SubstanceHistory />}
                            />
                            <Route
                              path="/dashboard"
                              element={<MainDashboard />}
                            />
                            <Route path="/" element={<MainDashboard />} />
                            <Route path="/adminList" element={<AdminList />} />
                            <Route
                              path="/tenantList"
                              element={<TenantList />}
                            />
                            <Route path="/userList" element={<UserList />} />
                            <Route path="/devices" element={<Device />} />
                            <Route
                              path="/deviceHistorty"
                              element={<DeviceHistoryPage />}
                            />
                            <Route
                              path="/deviceIntegration"
                              element={<DeviceIntegrationComponent />}
                            />
                            <Route
                              path="/deviceConfig"
                              element={<DeviceConfig />}
                            />
                            <Route
                              path="/deviceversion"
                              element={<DevicesVersionPage />}
                            />
                            <Route
                              path="/roleManagement"
                              element={<RoleManagement />}
                            />
                            <Route path="/location" element={<Location />} />
                            <Route path="/detection" element={<Detection />} />
                            <Route
                                  path="/category"
                                  element={<CategoryManagementPage />}
                                />
                            <Route path="/profile" element={<Profile />} />
                            <Route
                              path="/notifications"
                              element={<NotificationPage />}
                            />
                            {/* <Route
                              path="/dashboard"
                              element={
                                <DndProvider backend={HTML5Backend}>
                                  <Dashboard />
                                </DndProvider>
                              }
                            /> */}
                            <Route path="/eventMap" element={<EventMap />} />
                            <Route
                              path="/downloaddashboard"
                              element={<DashboardDownLoadPage />}
                            />
                            <Route path="/export/history" element={<ExportHistory />} />
                            <Route
                              path="/overview"
                              element={<OverviewPage />}
                            />
                            <Route
                              path="/summary/:substancekey"
                              element={<Summary />}
                            />
                            <Route path="/summary" element={<Summary />} />
                            <Route path="/query" element={<QueryPage />} />
                            <Route
                              path="/tilemanagement"
                              element={<TileManagerPage />}
                            />
                            <Route
                              path="/threshold"
                              element={<ThresholdPage />}
                            />
                            <Route
                              path="/internalIds"
                              element={<InternalIdsPage />}
                            />
                            <Route
                              path="/templates"
                              element={<EmailTemplatePage />}
                            />
                            <Route
                              path="/sendemail"
                              element={<SendEmailPage />}
                            />
                            <Route
                              path="/redirect-dashboard"
                              element={<RedirectionLoader />}
                            />
                            <Route path="/tenantreport" element={<TenantReportPage />} />
                            <Route path="/schedule-reports" element={<ScheduleReportListPage />} />
                            <Route path="/notification-rules" element={<NotificationRulesPage />} />
                            <Route path="/element-detection" element={<Outlet />} >
                              <Route path="" element={<DetectionPage />} />
                              <Route path=":substanceKey" element={<DetectionPage />} />
                            </Route>

                            {/* <Route path="/element-detection" element={<Outlet />}>
                              <Route path="" element={<DetectionPage />} />
                              <Route path=":substanceId" element={<DetectionPage />} />
                              <Route path=":substanceId/details" element={<ElementDetails />} />
                              <Route path=":substanceId/details/:facilityKey/:level/:location" element={<ElementDetails />} />
                            </Route> */}
                            <Route path="/facility/dashboard" element={<FacilityMainView />} />                            
                            <Route path="/ieq-gpt" element={<Outlet />} >
                              <Route path="" element={<IeqGPT />} />
                              <Route path="topics" element={<Topics />} />
                              <Route path="contents" element={<Contents />} />
                              <Route path="llm-configuration" element={<LLMConfiguration />} />
                            </Route>
                          </Routes>
                        </Layout >
                        <Routes>
                          <Route
                            path="/verify"
                            element={<VerifyLoginLinkComponent />}
                          />
                        </Routes>
                      </>
                    ) : (
                      <Layout>
                        <SsoDisabled />
                      </Layout>
                    )
                  ) : (
                    <Layout>
                      <LogoutScreen />
                    </Layout>
                  )
                ) : (
                  <>
                    <CustomLoader />
                    {/* <CircularProgress
                    sx={{ position: "fixed", top: "49%", left: "49%" }}
                  /> */}
                  </>
                )) : (
                <Routes>
                  <Route path="/" element={<Login />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgetPassword" element={<ForgetPassword />} />
                  <Route path="/signUp" element={<SignUp />} />
                  <Route
                    path="/federatedSignIn"
                    element={<FederatedSignIn />}
                  />
                  <Route
                    path="/sessionExpired"
                    element={<SessionExpiredPage />}
                  />
                  <Route
                    path="/verify"
                    element={<VerifyLoginLinkComponent isLogin={true} />}
                  />
                  <Route path="*" element={<Login />} />
                </Routes>
              )}
            </Suspense >
          </>
        ) : (
          <>
            <CustomLoader />
            {/* <CircularProgress
                    sx={{ position: "fixed", top: "49%", left: "49%" }}
                  /> */}
          </>
        )}
      </ThemeComponent >
    </ErrorBoundaryComponent >
  );
};

export default App;